import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Upload,
  Tag,
  Filter,
} from "lucide-react";
import api, {
  ApiResponse,
  getCategories,
  getAllPresetsAPI,
} from "../../services/api";
import { toast } from "react-hot-toast";
import { Info } from "lucide-react";
import PrerequisitesDialog from "../../components/PrerequisitesDialog";
import vegIcon from "../../assests/Veg_symbol.png";
import nonvegIcon from "../../assests/Non_veg_symbol.png";
import both from "../../assests/both.png";
import { activeCurrencySymbol } from "../../utils/currency";
interface CategoryCode {
  _id: string;
  code: string;
  name: string;
  taxable: boolean;
}

interface MenuItem {
  _id: string;
  name: string;
  price: number;
  category: string;
  categoryCode?: string;
  description: string;
  imageUrl: string;
  foodType: "veg" | "non-veg" | "both";
  tags?: string[];
  customize?: CustomGroup[];
}

interface Category {
  _id: string;
  name: string;
  categoryCode?: CategoryCode;
}

interface PortionOption {
  key: string;
  label: string;
  icon?: string;
}

interface Choice {
  label: string;
  price: number;
  default?: boolean;
  image?: string;
}

interface CustomGroup {
  name: string;
  required: boolean;
  multiple?: boolean;
  choices: Choice[];
  noOfFreeToppings?: number;
  maxToppingsAllowed?: number;
  minAddons?: number;
  displayType?: "checkbox" | "quantity" | "portion" | "radio";
  quantityOptions?: number[];
  portionOptions?: PortionOption[];
  showPortionSelector?: boolean;
  fromPreset?: boolean;
  presetId?: string;
}

interface Choices {
  _id: string;
  label: string;
  price: number;
  default: boolean;
}

interface CustomizationOption {
  _id: string;
  name: string;
  required: boolean;
  multiple: boolean;
  choices: Choices[];
  noOfFreeToppings?: number;
  maxToppingsAllowed?: number;
  minAddons?: number;
  displayType?: "checkbox" | "quantity" | "portion" | "radio";
  quantityOptions?: number[];
  portionOptions?: PortionOption[];
  showPortionSelector?: boolean;
}

interface PresetItem {
  _id: string;
  name: string;
  description: string;
  categoryId: string;
  category: string;
  isActive: boolean;
  customizationOptions: CustomizationOption[];
}

const MenuManagement: React.FC = () => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [, setGroupErrors] = useState<string[]>([]);
  const [presetData, setPresetData] = useState<PresetItem[]>([]);
  const [selectedPresetIds, setSelectedPresetIds] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    name: "",
    price: "",
    category: "",
    categoryCode: "",
    foodType: "",
    description: "",
    imageUrl: "",
    tags: "",
    customize: [] as CustomGroup[],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [catLoading, setCatLoading] = useState(true);
  const [categoryCodes, setCategoryCodes] = useState<CategoryCode[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingGroupIndex, setEditingGroupIndex] = useState<number | null>(
    null
  );

  useEffect(() => {
    console.log("Selected Preset Ids changed: ", categories);
  }, [categories]);

  const handleSave = (selectedIds: string[]) => {
    setSelectedPresetIds(selectedIds);

    // Convert presets → CustomGroups
    const selectedPresets = presetData.filter((p) =>
      selectedIds.includes(p._id)
    );

    const presetGroups: CustomGroup[] = selectedPresets.flatMap((preset) =>
      preset.customizationOptions.map((option) => ({
        name: option.name,
        required: option.required,
        multiple: option.multiple,
        choices: option.choices.map((choice) => ({
          label: choice.label,
          price: choice.price,
          default: choice.default,
        })),
        noOfFreeToppings: option.noOfFreeToppings,
        maxToppingsAllowed: option.maxToppingsAllowed,
        minAddons: option.minAddons,
        displayType: option.displayType || "checkbox",
        quantityOptions: option.quantityOptions || [0, 1, 2, 3],
        portionOptions: option.portionOptions || [
          { key: "left", label: "Left" },
          { key: "whole", label: "Whole" },
          { key: "right", label: "Right" },
        ],
        showPortionSelector: option.showPortionSelector || false,
        fromPreset: true,
        presetId: preset._id,
      }))
    );

    const normalizeGroupName = (name: string) => name.trim().toLowerCase();
    const getPresetGroupKey = (group: CustomGroup) =>
      `${group.presetId || ""}::${normalizeGroupName(group.name)}`;

    setFormData((prev) => {
      const filteredCustomize = prev.customize.filter((group) => {
        // Remove only groups that came from presets that are now unselected.
        if (group.fromPreset) {
          if (group.presetId) {
            return selectedIds.includes(group.presetId);
          }
          // Legacy preset groups without presetId: keep them only if a selected
          // preset contains a group with the same name.
          return presetGroups.some(
            (presetGroup) =>
              normalizeGroupName(presetGroup.name) ===
              normalizeGroupName(group.name)
          );
        }
        // Always keep manually added groups.
        return true;
      });

      // Deduplicate only against existing preset-origin groups by presetId+name.
      // This allows manual groups with same name and different presets to coexist.
      const existingPresetKeys = new Set(
        filteredCustomize
          .filter((group) => group.fromPreset)
          .map(getPresetGroupKey)
      );

      const newGroups = presetGroups.filter(
        (group) => !existingPresetKeys.has(getPresetGroupKey(group))
      );

      return {
        ...prev,
        customize: [...filteredCustomize, ...newGroups],
      };
    });
  };

  const [groupDraft, setGroupDraft] = useState<CustomGroup>({
    name: "",
    required: false,
    multiple: false,
    choices: [],
    noOfFreeToppings: undefined,
    maxToppingsAllowed: undefined,
    minAddons: undefined,
    displayType: "checkbox",
    quantityOptions: [0, 1, 2, 3],
    portionOptions: [
      { key: "left", label: "Left" },
      { key: "whole", label: "Whole" },
      { key: "right", label: "Right" },
    ],
    showPortionSelector: false,
    fromPreset: false,
  });
  const [quantityOptionsInput, setQuantityOptionsInput] = useState(
    "0, 1, 2, 3"
  );

  // Image upload for main item
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Image upload for choices
  const [, setChoiceFiles] = useState<{
    [key: string]: File | null;
  }>({});
  const [, setUploadingChoices] = useState<{
    [key: string]: boolean;
  }>({});

  // Function to handle the image upload for a choice
  const handleChoiceImageUpload = async (
    file: File,
    choiceKey: string
  ): Promise<string> => {
    const formDataObj = new FormData();
    formDataObj.append("image", file);

    setUploadingChoices((prev) => ({ ...prev, [choiceKey]: true }));

    try {
      const response = await api.post<{ url: string }>(
        "/upload-image",
        formDataObj,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data.url;
    } catch (err) {
      console.error("Error uploading image", err);
      throw new Error("Image upload failed");
    } finally {
      setUploadingChoices((prev) => ({ ...prev, [choiceKey]: false }));
    }
  };

  useEffect(() => {
    fetchMenuItems();
    fetchCategories();
    fetchCategoryCodes();
  }, []);

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<ApiResponse<MenuItem[]>>("/menu");
      if (response.data.success && Array.isArray(response.data.data)) {
        setMenuItems(response.data.data);
      } else {
        setMenuItems([]);
        setError("Invalid data format received from server");
      }
    } catch {
      setError("Failed to load menu items. Please try again.");
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCatLoading(true);
    try {
      const res = await getCategories();
      setCategories(res.data.data);
    } catch {
      setCategories([]);
    } finally {
      setCatLoading(false);
    }
  };

  const fetchCategoryCodes = async () => {
    try {
      const res = await api.get<CategoryCode[]>("/codes");
      if (Array.isArray(res.data)) {
        setCategoryCodes(res.data);
      }
    } catch {
      setCategoryCodes([]);
    }
  };

  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const res = await getAllPresetsAPI();
        const data = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        setPresetData(data);
      } catch (error) {
        console.error("Error fetching presets:", error);
      }
    };
    fetchPresets();
  }, []);

  // Helper: get the default categoryCode id for a given category name
  const getCategoryCodeId = (categoryName: string): string => {
    const cat = categories.find((c) => c.name === categoryName);
    return (cat?.categoryCode as CategoryCode | undefined)?._id || "";
  };

  const getMatchedPresetIds = (
    presets: PresetItem[],
    customGroups: CustomGroup[]
  ): string[] => {
    const groupNames = new Set(
      customGroups.map((g) => g.name.trim().toLowerCase())
    );

    return presets
      .filter((preset) =>
        preset.customizationOptions.some((opt) =>
          groupNames.has(opt.name.trim().toLowerCase())
        )
      )
      .map((preset) => preset._id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const tagsArray = formData.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag !== "");

    const uploadedUrl = await uploadImage();

    const itemData = {
      ...formData,
      price: parseFloat(formData.price),
      imageUrl: uploadedUrl,
      tags: tagsArray.length > 0 ? tagsArray : undefined,
      categoryId:
        categories.find((cat) => cat.name === formData.category)?._id || null,
      categoryCode: formData.categoryCode || null,
    };

    try {
      setError(null);
      if (editingItem) {
        await api.put<ApiResponse<MenuItem>>(
          `/menu/${editingItem._id}`,
          itemData
        );
        toast.success("Menu item updated successfully!");
      } else {
        await api.post<ApiResponse<MenuItem>>("/menu", itemData);
        toast.success("Menu item added successfully!");
      }
      await fetchMenuItems();
      resetForm();
      setShowModal(false);
    } catch {
      setError("Failed to save menu item. Please try again.");
      toast.error("Failed to save menu item.");
    }
  };

  const uploadImage = async (): Promise<string> => {
    if (!file) return formData.imageUrl;
    const formDataObj = new FormData();
    formDataObj.append("image", file);
    setUploading(true);
    try {
      const res = await api.post<{ url: string }>(
        "/upload-image",
        formDataObj,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setUploading(false);
      setFile(null);
      return res.data.url;
    } catch (err) {
      setUploading(false);
      console.error("Image upload failed:", err);
      return "";
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      price: "",
      category: "",
      categoryCode: "",
      foodType: "",
      description: "",
      imageUrl: "",
      tags: "",
      customize: [],
    });
    setFile(null);
    setChoiceFiles({});
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        setError(null);
        await api.delete<ApiResponse<{}>>(`/menu/${id}`);
        await fetchMenuItems();
        toast.success("Menu item deleted successfully!");
      } catch {
        setError("Failed to delete menu item. Please try again.");
        toast.error("Failed to delete menu item.");
      }
    }
  };
  const validateGroupDraft = () => {
    const errors: string[] = [];

    // Group name
    if (!groupDraft.name.trim()) {
      errors.push("Group name is required.");
    }

    // Choices existence
    if (!groupDraft.choices.length) {
      errors.push("At least one choice is required.");
    }

    // Choice validations
    groupDraft.choices.forEach((choice, index) => {
      if (!choice.label?.trim()) {
        errors.push(`Choice ${index + 1}: Label is required.`);
      }

      if (
        choice.price === undefined ||
        choice.price === null ||
        choice.price < 0
      ) {
        errors.push(`Choice ${index + 1}: Price must be 0 or greater.`);
      }
    });

    // Required logic
    // if (groupDraft.required && defaultChoices.length !== 1) {
    //   errors.push("Required group must have exactly one default choice.");
    // }

    // Multiple choice logic
    // if (groupDraft.multiple && defaultChoices.length < 1) {
    //   errors.push("Multiple choice group must have at least one default choice.");
    // }

    setGroupErrors(errors);
    return errors.length === 0;
  };

  const handleEdit = (item: MenuItem) => {
    setEditingItem(item);

    const customizeGroups = item.customize || [];

    setFormData({
      name: item.name,
      price: item.price.toString(),
      category: item.category,
      categoryCode: item.categoryCode || getCategoryCodeId(item.category),
      foodType: item.foodType || "",
      description: item.description || "",
      imageUrl: item.imageUrl,
      tags: item.tags ? item.tags.join(", ") : "",
      customize: customizeGroups,
    });

    // Use presetId-based detection if groups have it (accurate), otherwise fall back to name matching
    const presetIdBased = Array.from(new Set(
      customizeGroups.filter(g => g.fromPreset && g.presetId).map(g => g.presetId!)
    ));
    const matchedPresetIds = presetIdBased.length > 0
      ? presetIdBased
      : getMatchedPresetIds(presetData, customizeGroups);

    setSelectedPresetIds(matchedPresetIds);

    setShowModal(true);
  };

  useEffect(() => {
    if (!editingItem || !presetData.length) return;

    const presetIdBased = Array.from(new Set(
      formData.customize.filter(g => g.fromPreset && g.presetId).map(g => g.presetId!)
    ));
    const matchedPresetIds = presetIdBased.length > 0
      ? presetIdBased
      : getMatchedPresetIds(presetData, formData.customize);

    setSelectedPresetIds(matchedPresetIds);
  }, [presetData]);

  const openAddModal = () => {
    resetForm();
    setSelectedPresetIds([]);
    setShowModal(true);
  };

  // Filter and pagination logic
  // Filter and pagination logic
  const filteredItems = menuItems.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      selectedCategory === "" || item.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = filteredItems.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  if (loading || catLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto p-4 pt-0 lg:p-8 flex flex-col overflow-y-auto no-scrollbar h-screen">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Menu Management</h1>

          {/* Right side controls */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            {/* Category Filter */}
            <div className="relative min-w-48">
              <Filter
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10"
                size={16}
              />
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-9 pr-8 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white appearance-none text-sm relative z-0"
                style={{
                  backgroundImage: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                }}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none z-10">
                <svg
                  className="w-3 h-3 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            {/* Add Menu Item Button */}
            <button
              onClick={openAddModal}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition flex items-center gap-2 whitespace-nowrap"
            >
              <Plus size={18} />
              Add Menu Item
            </button>
          </div>
        </div>

        {/* ================= SEARCH ================= */}
        <div className="rounded-lg mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search Menu Items
          </label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
              placeholder="Search by name or category..."
              className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
      {/* Active filter indicator */}
      {/* {selectedCategory && (
    <div className="mb-4 flex items-center gap-2">
      <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
        <Filter size={12} />
        {selectedCategory}
        <button
          onClick={() => {
            setSelectedCategory("");
            setCurrentPage(1);
          }}
          className="ml-1 text-blue-600 hover:text-blue-800"
        >
          <X size={12} />
        </button>
      </span>
    </div>
  )} */}
      {/* ================= ERROR ================= */}
      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">
          {error}
        </div>
      )}

      {/* ================= CONTENT ================= */}
      <div className="flex-1">
        {paginatedItems.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No menu items found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different term"
                  : "Add your first menu item to get started"}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {paginatedItems.map((item) => (
              <div
                key={item._id}
                className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col h-full"
              >
                {/* Image */}
                <div className="aspect-square w-full h-48 bg-gray-200 relative overflow-hidden flex-shrink-0">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <Upload size={32} />
                    </div>
                  )}

                  {/* Food Type Badge */}
                  <div className="absolute top-2 left-2">
                    {item.foodType === "veg" && (
                      <div className="w-3 h-3 border-2 border-green-600 bg-green-100 rounded-sm"></div>
                    )}
                    {item.foodType === "non-veg" && (
                      <div className="w-3 h-3 border-2 border-red-600 bg-red-100 rounded-sm">
                        <div className="w-1 h-1 bg-red-600 rounded-full mx-auto mt-0.5"></div>
                      </div>
                    )}
                    {item.foodType === "both" && (
                      <div className="w-3 h-3 border-2 border-orange-600 bg-orange-100 rounded-sm">
                        <div className="w-1 h-1 bg-orange-600 rounded-full mx-auto mt-0.5"></div>
                      </div>
                    )}
                  </div>

                  {/* Price Badge */}
                  <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded text-sm font-semibold">
                    {activeCurrencySymbol()}{item.price.toFixed(2)}
                  </div>
                </div>

                {/* Content */}
                <div className="p-3 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold text-gray-800 line-clamp-1 mb-1">
                    {item.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">{item.category}</p>

                  {item.description && (
                    <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                      {item.description}
                    </p>
                  )}

                  {/* Tags */}
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {item.tags.slice(0, 2).map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs"
                        >
                          <Tag size={10} />
                          {tag}
                        </span>
                      ))}
                      {item.tags.length > 2 && (
                        <span className="text-xs text-gray-400">
                          +{item.tags.length - 2} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 mt-auto">
                    <button
                      onClick={() => handleEdit(item)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item._id)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {filteredItems.length > 0 && totalPages > 1 && (
        <div className="py-4 flex justify-center ">
          <div className="flex gap-2 items-center">
            {/* Previous Button */}
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>

            {/* Page Numbers */}
            {(() => {
              const pages = [];
              const showEllipsis = totalPages > 7;

              if (!showEllipsis) {
                // Show all pages if 7 or fewer
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === i
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {i}
                    </button>
                  );
                }
              } else {
                // Smart pagination for many pages
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, currentPage + 2);

                // Always show first page
                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      onClick={() => setCurrentPage(1)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === 1
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      1
                    </button>
                  );

                  if (startPage > 2) {
                    pages.push(
                      <span key="ellipsis1" className="px-2 py-1 text-gray-500">
                        ...
                      </span>
                    );
                  }
                }

                // Show pages around current page
                for (let i = startPage; i <= endPage; i++) {
                  if (i !== 1 && i !== totalPages) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1 rounded transition ${
                          currentPage === i
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 hover:bg-gray-300"
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                }

                // Always show last page
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span key="ellipsis2" className="px-2 py-1 text-gray-500">
                        ...
                      </span>
                    );
                  }

                  pages.push(
                    <button
                      key={totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === totalPages
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {totalPages}
                    </button>
                  );
                }
              }

              return pages;
            })()}

            {/* Next Button */}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowModal(false)}
          />

          {/* Modal Container */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col mx-auto no-scrollbar">
            {/* Modal Header - Fixed */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800 mb-0">
                {editingItem ? "Edit Menu Item" : "Add New Menu Item"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto no-scrollbar">
              <form id="menu-form" onSubmit={handleSubmit} className="p-6">
                {/* Basic Information Section */}
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Item Name *
                      </label>
                      <input
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        type="text"
                        placeholder="e.g., Margherita Pizza"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Price *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          $
                        </span>
                        <input
                          className="w-full p-2.5 pl-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          placeholder="0.00"
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={formData.price}
                          onChange={(e) =>
                            setFormData({ ...formData, price: e.target.value })
                          }
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Category *
                      </label>
                      <select
                        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                        value={formData.category}
                        onChange={(e) => {
                          const newCategory = e.target.value;
                          const defaultCode = getCategoryCodeId(newCategory);
                          setFormData({
                            ...formData,
                            category: newCategory,
                            categoryCode: defaultCode,
                          });
                        }}
                        required
                      >
                        <option value="">Select a category</option>
                        {categories.map((cat) => (
                          <option key={cat._id} value={cat.name}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Food Type *
                      </label>
                      <div className="flex gap-6 items-center">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="foodType"
                            value="veg"
                            checked={formData.foodType === "veg"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                foodType: e.target.value,
                              })
                            }
                            required
                          />

                          <img
                            src={vegIcon}
                            alt={"veg"}
                            className="inline-block w-4 h-4 ml-1"
                          />
                          <span> Vegetarian</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="foodType"
                            value="non-veg"
                            checked={formData.foodType === "non-veg"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                foodType: e.target.value,
                              })
                            }
                          />

                          <img
                            src={nonvegIcon}
                            alt={"veg"}
                            className="inline-block w-4 h-4 ml-1"
                          />
                          <span>NonVegetarian</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="foodType"
                            value="both"
                            checked={formData.foodType === "both"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                foodType: e.target.value,
                              })
                            }
                          />

                          <img
                            src={both}
                            alt={"both"}
                            className="inline-block w-4 h-4 ml-1"
                          />
                          <span>Both</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Category Code */}
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Category Code
                      <span className="text-gray-400 text-xs ml-2">
                        (pre-filled from category, change to override for this item)
                      </span>
                    </label>
                    <select
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                      value={formData.categoryCode}
                      onChange={(e) =>
                        setFormData({ ...formData, categoryCode: e.target.value })
                      }
                    >
                      <option value="">-- Select a category first --</option>
                      {categoryCodes.map((code) => (
                        <option key={code._id} value={code._id}>
                          {code.code} – {code.name}
                          {code.taxable ? " (Taxable)" : " (Non-taxable)"}
                        </option>
                      ))}
                    </select>
                    {(() => {
                      const selected = categoryCodes.find((c) => c._id === formData.categoryCode);
                      if (!selected) return null;
                      const cat = categories.find((c) => c.name === formData.category);
                      const catDefaultId = (cat?.categoryCode as CategoryCode | undefined)?._id;
                      const isOverride = catDefaultId && selected._id !== catDefaultId;
                      return (
                        <p className={`text-xs mt-1 font-medium ${selected.taxable ? "text-orange-600" : "text-green-600"}`}>
                          {selected.taxable ? "Tax will be applied" : "No tax"} on this item
                          {isOverride && (
                            <span className="ml-2 text-blue-500">(overriding category default)</span>
                          )}
                        </p>
                      );
                    })()}
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      rows={3}
                      placeholder="Describe your menu item..."
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Tags
                      <span className="text-gray-500 text-xs ml-2">
                        (separate by commas)
                      </span>
                    </label>
                    <input
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      type="text"
                      placeholder="e.g., spicy, popular, chef's special"
                      value={formData.tags}
                      onChange={(e) =>
                        setFormData({ ...formData, tags: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Upload Image *
                    </label>
                    <div className="relative">
                      <input
                        className="w-full p-2.5 border-2 border-dashed border-gray-300 rounded-md focus:border-blue-500 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0])
                            setFile(e.target.files[0]);
                        }}
                        // required={!formData.imageUrl}
                      />
                    </div>
                    {uploading && (
                      <p className="text-blue-600 text-sm mt-2 flex items-center">
                        Uploading image...
                      </p>
                    )}
                  </div>
                </div>

                {/* Customizations Section */}
                {/* ================= CUSTOMIZATIONS ================= */}
                <div className="mt-6 border-t border-gray-200 pt-6 item">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Customizations
                    </h3>

                    <div className="justify-end flex flex-row ">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingGroupIndex(null);
                            setGroupDraft({
                            name: "",
                            required: false,
                            multiple: false,
                            choices: [],
                            noOfFreeToppings: undefined,
                            maxToppingsAllowed: undefined,
                            minAddons: undefined,
                            displayType: "checkbox",
                            quantityOptions: [0, 1, 2, 3],
                            portionOptions: [
                              { key: "left", label: "Left" },
                              { key: "whole", label: "Whole" },
                              { key: "right", label: "Right" },
                              ],
                              showPortionSelector: false,
                            });
                            setQuantityOptionsInput("0, 1, 2, 3");
                            setShowGroupModal(true);
                          }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2 text-sm"
                      >
                        <Plus size={16} />
                        Add Group
                      </button>

                      {presetData.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setOpen(true)}
                          className="bg-blue-600 text-white px-4 py-2 ml-2 rounded-md flex items-center gap-2 text-sm"
                        >
                          <Plus size={16} />
                          {selectedPresetIds.length > 0
                            ? `Add from presets (${selectedPresetIds.length})`
                            : "Add from presets"}
                        </button>
                      )}
                    </div>
                  </div>
                  {formData.customize.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-md border-2 border-dashed border-gray-300">
                      <p className="text-gray-500 text-sm">
                        No customization groups added yet
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left">Group Name</th>
                            <th className="px-4 py-3 text-left">Type</th>
                            <th className="px-4 py-3 text-left">Limits</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                          </tr>
                        </thead>

                        <tbody className="divide-y">
                          {formData.customize.map((group, index) => (
                            <tr key={index}>
                              <td className="px-4 py-3 font-medium">
                                {group.name}
                              </td>

                              <td className="px-4 py-3 text-gray-600">
                                {group.required
                                  ? "Required (Single)"
                                  : group.multiple
                                  ? "Optional (Multiple)"
                                  : "Optional (Single)"}
                              </td>

                              <td className="px-4 py-3 text-gray-500 text-xs space-y-0.5">
                                {(group.minAddons ?? 0) > 0 && (
                                  <div>Min: {group.minAddons}</div>
                                )}
                                {group.maxToppingsAllowed !== undefined && (
                                  <div>Max: {group.maxToppingsAllowed}</div>
                                )}
                                {(group.noOfFreeToppings ?? 0) > 0 && (
                                  <div className="text-green-600">Free: {group.noOfFreeToppings}</div>
                                )}
                                {!(group.minAddons ?? 0) && group.maxToppingsAllowed === undefined && !(group.noOfFreeToppings ?? 0) && (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>

                              <td className="px-4 py-3 flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingGroupIndex(index);
                                    setGroupDraft(group);
                                    setQuantityOptionsInput(
                                      (group.quantityOptions || [0, 1, 2, 3]).join(
                                        ", "
                                      )
                                    );
                                    setShowGroupModal(true);
                                  }}
                                  className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md"
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...formData.customize];
                                    updated.splice(index, 1);
                                    setFormData({
                                      ...formData,
                                      customize: updated,
                                    });
                                  }}
                                  className="px-3 py-1.5 bg-red-50 text-red-600 rounded-md"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="menu-form"
                className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                disabled={uploading}
              >
                {uploading ? (
                  <span className="flex items-center gap-2">Uploading...</span>
                ) : editingItem ? (
                  "Update Item"
                ) : (
                  "Add Item"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ================= GROUP MODAL ================= */}
      {showGroupModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowGroupModal(false)}
          />

          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl">
            {/* Header */}
            <div className="border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                {editingGroupIndex !== null ? "Edit Group" : "Add Group"}
              </h3>
              <button onClick={() => setShowGroupModal(false)}>
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <input
                className="w-full p-2.5 border rounded-md"
                placeholder="Group Name (e.g. Size, Cheese)"
                value={groupDraft.name}
                onChange={(e) =>
                  setGroupDraft({ ...groupDraft, name: e.target.value })
                }
              />

              <div className="flex items-center gap-8">
                {/* Required */}
                <div className="relative flex items-center gap-1 group">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={groupDraft.required}
                      onChange={(e) =>
                        setGroupDraft({
                          ...groupDraft,
                          required: e.target.checked,
                          multiple: e.target.checked
                            ? false
                            : groupDraft.multiple,
                        })
                      }
                    />
                    Required
                  </label>

                  {/* Tooltip */}
                  <div
                    className="absolute left-0 top-full mt-2 hidden group-hover:block z-[9999]
                    w-56 rounded-md bg-black text-white text-xs px-3 py-2 shadow-lg"
                  >
                    Allows user to select <b>only one</b> option from choices
                  </div>
                </div>

                {/* Multiple */}
                <div className="relative flex items-center gap-1 group">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={groupDraft.multiple || false}
                      onChange={(e) =>
                        setGroupDraft({
                          ...groupDraft,
                          multiple: e.target.checked,
                          required: e.target.checked
                            ? false
                            : groupDraft.required,
                        })
                      }
                    />
                    Multiple Choice
                  </label>

                  {/* Tooltip */}
                  <div
                    className="absolute left-0 top-full mt-2 hidden group-hover:block z-[9999]
                    w-64 rounded-md bg-black text-white text-xs px-3 py-2 shadow-lg"
                  >
                    Allows user to select <b>multiple</b> options from choices
                  </div>
                </div>

                {/* Default info */}
                {!groupDraft.required && !groupDraft.multiple && (
                  <div className="relative flex items-center gap-1 group text-sm text-gray-500">
                    <Info
                      size={14}
                      className="text-gray-400 cursor-pointer hover:text-gray-600 transition"
                    />
                    <span className="cursor-pointer">Info</span>

                    {/* Tooltip */}
                    <div
                      className="absolute left-0 top-full mt-2 hidden group-hover:block z-[9999]
                      w-72 rounded-md bg-black text-white text-xs px-3 py-2 shadow-lg"
                    >
                      If any option is not selected, allows user to choose the{" "}
                      <b>quantity/count</b> of a particular choice
                    </div>
                  </div>
                )}
              </div>

              {/* Addon Limits - show for all non-required groups */}
              {!groupDraft.required && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="text-sm font-semibold mb-3">Addon Limits</h4>
                <div className="grid grid-cols-3 gap-3">
                  {/* Min Addons */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Min Addons
                      <span className="text-gray-400 ml-1">(0 = optional)</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      className="w-full p-2 border rounded-md text-sm"
                      value={groupDraft.minAddons ?? ""}
                      onChange={(e) =>
                        setGroupDraft({
                          ...groupDraft,
                          minAddons: e.target.value !== "" ? parseInt(e.target.value, 10) : undefined,
                        })
                      }
                    />
                  </div>
                  {/* Max Addons */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Max Addons
                      <span className="text-gray-400 ml-1">(blank = unlimited)</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="unlimited"
                      className={`w-full p-2 border rounded-md text-sm ${
                        groupDraft.maxToppingsAllowed !== undefined &&
                        groupDraft.noOfFreeToppings !== undefined &&
                        groupDraft.maxToppingsAllowed <= groupDraft.noOfFreeToppings
                          ? "border-red-400"
                          : "border-gray-300"
                      }`}
                      value={groupDraft.maxToppingsAllowed ?? ""}
                      onChange={(e) =>
                        setGroupDraft({
                          ...groupDraft,
                          maxToppingsAllowed: e.target.value !== "" ? parseInt(e.target.value, 10) : undefined,
                        })
                      }
                    />
                    {groupDraft.maxToppingsAllowed !== undefined &&
                      groupDraft.noOfFreeToppings !== undefined &&
                      groupDraft.maxToppingsAllowed <= groupDraft.noOfFreeToppings && (
                        <p className="text-xs text-red-500 mt-1">Must be greater than free items</p>
                      )}
                  </div>
                  {/* Free Toppings - only for quantity groups */}
                  {!groupDraft.multiple && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Free Items
                        <span className="text-gray-400 ml-1">(included free)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        className="w-full p-2 border rounded-md text-sm"
                        value={groupDraft.noOfFreeToppings ?? ""}
                        onChange={(e) =>
                          setGroupDraft({
                            ...groupDraft,
                            noOfFreeToppings: e.target.value !== "" ? parseInt(e.target.value, 10) : undefined,
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Display Settings - Only show when not required and not multiple (customization mode) */}
              {!groupDraft.required && !groupDraft.multiple && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="text-sm font-semibold mb-3">Display Settings</h4>

                {/* Quantity Options */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity Options (comma separated numbers)
                  </label>
                  <input
                    className="w-full p-2.5 border rounded-md"
                    placeholder="e.g., 0, 1, 2, 3 or -1, 0, 1, 2"
                    value={quantityOptionsInput}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      setQuantityOptionsInput(rawValue);
                      const values = e.target.value
                        .split(",")
                        .map((v) => parseInt(v.trim()))
                        .filter((v) => !isNaN(v));
                      setGroupDraft({
                        ...groupDraft,
                        quantityOptions: values.length > 0 ? values : [0, 1, 2, 3],
                      });
                    }}
                    onBlur={() =>
                      setQuantityOptionsInput(
                        (groupDraft.quantityOptions || [0, 1, 2, 3]).join(", ")
                      )
                    }
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    For default toppings use -1, 0, 1, 2 (where -1 means "none", 0 is default). For extras use 0, 1, 2, 3.
                  </p>
                </div>

                {/* Show Portion Selector Toggle */}
                <div className="mb-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={groupDraft.showPortionSelector || false}
                      onChange={(e) =>
                        setGroupDraft({
                          ...groupDraft,
                          showPortionSelector: e.target.checked,
                        })
                      }
                    />
                    Show Portion Selector (Left/Right/Whole for Half & Half pizzas)
                  </label>
                </div>

                {/* Portion Options - Only show when showPortionSelector is true */}
                {groupDraft.showPortionSelector && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Portion Options
                    </label>
                    <div className="space-y-2">
                      {(groupDraft.portionOptions || []).map((option, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <input
                            className="flex-1 p-2 border rounded-md text-sm"
                            placeholder="Key (e.g., left)"
                            value={option.key}
                            onChange={(e) => {
                              const updated = [...(groupDraft.portionOptions || [])];
                              updated[idx] = { ...updated[idx], key: e.target.value };
                              setGroupDraft({ ...groupDraft, portionOptions: updated });
                            }}
                          />
                          <input
                            className="flex-1 p-2 border rounded-md text-sm"
                            placeholder="Label (e.g., Left Half)"
                            value={option.label}
                            onChange={(e) => {
                              const updated = [...(groupDraft.portionOptions || [])];
                              updated[idx] = { ...updated[idx], label: e.target.value };
                              setGroupDraft({ ...groupDraft, portionOptions: updated });
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (groupDraft.portionOptions || []).filter(
                                (_, i) => i !== idx
                              );
                              setGroupDraft({ ...groupDraft, portionOptions: updated });
                            }}
                            className="text-red-600 text-sm px-2"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setGroupDraft({
                            ...groupDraft,
                            portionOptions: [
                              ...(groupDraft.portionOptions || []),
                              { key: "", label: "" },
                            ],
                          });
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add Portion Option
                      </button>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Choices Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Choice</th>
                      <th className="px-4 py-2 text-left">Price</th>
                      <th className="px-4 py-2 text-center">Default</th>
                      <th className="px-4 py-2 text-left">Image</th>
                      <th className="px-4 py-2 text-right">Action</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y">
                    {groupDraft.choices.map((choice, index) => (
                      <tr key={index}>
                        {/* Label */}
                        <td className="px-4 py-2">
                          <input
                            className="w-full border p-2 rounded"
                            placeholder="Label"
                            value={choice.label}
                            onChange={(e) => {
                              const updated = [...groupDraft.choices];
                              updated[index].label = e.target.value;
                              setGroupDraft({
                                ...groupDraft,
                                choices: updated,
                              });
                            }}
                          />
                        </td>
                        {/* Price */}
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            className="w-full border p-2 rounded"
                            placeholder="0.00"
                            value={
                              choice.price === 0 && (choice.price as any) !== ""
                                ? ""
                                : choice.price
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              const updated = [...groupDraft.choices];
                              if (val === "") {
                                updated[index].price = "" as any;
                              } else {
                                const num = Number(val);
                                if (num < 0) return;
                                updated[index].price = num;
                              }
                              setGroupDraft({
                                ...groupDraft,
                                choices: updated,
                              });
                            }}
                          />
                        </td>

                        {/* Default */}
                        <td className="px-4 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={choice.default || false}
                            onChange={(e) => {
                              const updated = [...groupDraft.choices];

                              if (e.target.checked) {
                                // Enforce single default when required
                                if (groupDraft.required) {
                                  updated.forEach((c, i) => {
                                    c.default = i === index;
                                  });
                                } else {
                                  updated[index].default = true;
                                }
                              } else {
                                updated[index].default = false;
                              }

                              setGroupDraft({
                                ...groupDraft,
                                choices: updated,
                              });
                            }}
                          />
                        </td>

                        {/* Image */}
                        <td className="px-4 py-2">
                          <label className="block">
                            <input
                              type="file"
                              accept="image/png, image/jpeg, image/webp"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;

                                const key = `group-${
                                  editingGroupIndex ?? "new"
                                }-${index}`;

                                try {
                                  const url = await handleChoiceImageUpload(
                                    file,
                                    key
                                  );
                                  const updated = [...groupDraft.choices];
                                  updated[index].image = url;
                                  setGroupDraft({
                                    ...groupDraft,
                                    choices: updated,
                                  });
                                } catch (err) {
                                  console.error(
                                    "Choice image upload failed",
                                    err
                                  );
                                }
                              }}
                            />

                            {/* Upload Box */}
                            {!choice.image ? (
                              <div className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 rounded-md p-3 text-xs text-gray-500 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition">
                                <Upload size={16} className="text-blue-500" />
                                <span className="text-center leading-tight">
                                  Upload Image
                                </span>
                              </div>
                            ) : (
                              <div className="relative cursor-pointer w-12 h-12 group">
                                <img
                                  src={choice.image}
                                  alt="choice"
                                  className="w-full h-full object-cover rounded-md border"
                                />

                                {/* Replace overlay */}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-md transition">
                                  <span className="text-[10px] text-white">
                                    Change
                                  </span>
                                </div>
                              </div>
                            )}
                          </label>
                        </td>

                        {/* Remove */}
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => {
                              const updated = [...groupDraft.choices];
                              updated.splice(index, 1);
                              setGroupDraft({
                                ...groupDraft,
                                choices: updated,
                              });
                            }}
                            className="text-red-600 text-sm"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                onClick={() => {
                  const lastChoice =
                    groupDraft.choices[groupDraft.choices.length - 1];

                  if (lastChoice && !lastChoice.label.trim()) {
                    setGroupErrors([
                      "Please fill the previous choice before adding a new one.",
                    ]);
                    return;
                  }

                  setGroupDraft({
                    ...groupDraft,
                    choices: [...groupDraft.choices, { label: "", price: 0 }],
                  });
                }}
                className="w-full py-2 bg-gray-100 rounded-md"
              >
                + Add Choice
              </button>
            </div>

            {/* Footer */}
            <div className="border-t px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowGroupModal(false)}
                className="px-4 py-2 bg-gray-200 rounded-md"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  if (!validateGroupDraft()) return;

                  const updated = [...formData.customize];

                  if (editingGroupIndex !== null) {
                    updated[editingGroupIndex] = groupDraft;
                  } else {
                    updated.push(groupDraft);
                  }

                  setFormData({ ...formData, customize: updated });
                  setShowGroupModal(false);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md"
              >
                Save Group
              </button>
            </div>
          </div>
        </div>
      )}

      <PrerequisitesDialog
        open={open}
        onOpenChange={setOpen}
        data={presetData}
        onSave={handleSave}
        selectedIds={selectedPresetIds}
      />
    </div>
  );
};

export default MenuManagement;
